import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from "@backstage/theme";
import { appviaBrandColors, getThemeConfig } from "./unified-theme-config";

const themeConfig = getThemeConfig("light");

export const appviaTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: themeConfig.colors.primary.main,
        light: themeConfig.colors.primary.light,
        dark: themeConfig.colors.primary.dark,
      },
      secondary: {
        main: themeConfig.colors.secondary.main,
        light: themeConfig.colors.secondary.light,
        dark: themeConfig.colors.secondary.dark,
      },
      error: {
        main: themeConfig.colors.error.main,
        light: themeConfig.colors.error.light,
        dark: themeConfig.colors.error.dark,
      },
      warning: {
        main: themeConfig.colors.warning.main,
        light: themeConfig.colors.warning.light,
        dark: themeConfig.colors.warning.dark,
      },
      info: {
        main: themeConfig.colors.info.main,
        light: themeConfig.colors.info.light,
        dark: themeConfig.colors.info.dark,
      },
      success: {
        main: themeConfig.colors.success.main,
        light: themeConfig.colors.success.light,
        dark: themeConfig.colors.success.dark,
      },
      background: {
        default: themeConfig.colors.background.default,
        paper: themeConfig.colors.background.paper,
      },

      // Backstage specific
      banner: {
        info: themeConfig.colors.info.main,
        error: themeConfig.colors.error.main,
        text: themeConfig.colors.text.primary,
        link: themeConfig.colors.primary.light,
      },
      errorBackground: themeConfig.colors.error.light,
      warningBackground: themeConfig.colors.warning.light,
      infoBackground: themeConfig.colors.info.light,
      navigation: themeConfig.colors.navigation,
    },
    fontFamily: themeConfig.typography.fontFamily,
    htmlFontSize: themeConfig.typography.htmlFontSize,
  }),
  defaultPageTheme: "home",
  fontFamily: themeConfig.typography.fontFamily,

  pageTheme: {
    home: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave2,
    }),
    tool: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [
        themeConfig.colors.primary.main,
        themeConfig.colors.secondary.main,
      ],
      shape: shapes.wave,
    }),
  },

  components: {
    // Add CSS variables to the root - this must remain global
    MuiCssBaseline: {
      styleOverrides: () => `
        :root {
          /* Expose all theme colors as CSS variables */
          --appvia-primary-main: ${themeConfig.colors.primary.main};
          --appvia-primary-light: ${themeConfig.colors.primary.light};
          --appvia-primary-dark: ${themeConfig.colors.primary.dark};

          --appvia-secondary-main: ${themeConfig.colors.secondary.main};
          --appvia-secondary-light: ${themeConfig.colors.secondary.light};
          --appvia-secondary-dark: ${themeConfig.colors.secondary.dark};

          --appvia-error-main: ${themeConfig.colors.error.main};
          --appvia-warning-main: ${themeConfig.colors.warning.main};
          --appvia-info-main: ${themeConfig.colors.info.main};
          --appvia-success-main: ${themeConfig.colors.success.main};

          --appvia-text-primary: ${themeConfig.colors.text.primary};
          --appvia-text-secondary: ${themeConfig.colors.text.secondary};
          --appvia-text-disabled: ${themeConfig.colors.text.disabled};

          --appvia-background-default: ${themeConfig.colors.background.default};
          --appvia-background-paper: ${themeConfig.colors.background.paper};

          --appvia-divider: ${themeConfig.colors.divider};

          /* Chart colors */
          ${
        themeConfig.charts.map((color, i) =>
          `--appvia-chart-${i + 1}: ${color};`
        ).join("\n          ")
      }

          /* Spacing */
          --appvia-spacing-unit: ${themeConfig.spacing.unit}px;
          --appvia-radius-sm: ${themeConfig.spacing.radius.sm}px;
          --appvia-radius-md: ${themeConfig.spacing.radius.md}px;
          --appvia-radius-lg: ${themeConfig.spacing.radius.lg}px;
          --appvia-radius-xl: ${themeConfig.spacing.radius.xl}px;

          /* Shadows */
          ${
        themeConfig.shadows.map((shadow, i) =>
          `--appvia-shadow-${i}: ${shadow};`
        ).join("\n          ")
      }
        }

        /* Apply theme background to body */
        body {
          background-color: ${themeConfig.colors.background.default};
          font-family: ${themeConfig.typography.fontFamily};
        }

        /* Ensure MUI Typography components inherit font family */
        .MuiTypography-root {
          font-family: inherit;
        }

        /* Monospace elements should use mono font */
        code, pre, kbd, samp, tt {
          font-family: ${themeConfig.typography.monoFamily};
        }
      `,
    },

    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.primary.main,
          textDecoration: "none",
          transition: theme.transitions.create(["color"], {
            duration: theme.transitions.duration.shortest,
          }),
          "&:hover, &:focus, &:active": {
            color: theme.palette.primary.dark,
            textDecoration: "none",
          },
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderCollapse: "separate",
          borderSpacing: 0,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius * 2,
          overflow: "hidden",
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          transition: theme.transitions.create("background-color", {
            duration: theme.transitions.duration.shortest,
          }),
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&:last-of-type": {
            borderBottom: "none",
          },
          "&.MuiTableRow-head": {
            backgroundColor: theme.palette.background.default,
          },
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.secondary,
          fontWeight: theme.typography.fontWeightMedium,
          fontSize: "0.8125rem",
          textTransform: "none",
          letterSpacing: 0,
          padding: theme.spacing(2, 3),
          minWidth: theme.spacing(15),
          borderBottom: "none",
          borderTop: "none",
          borderLeft: "none",
          borderRight: "none",
          lineHeight: 1.5,
          "&:hover .MuiTableSortLabel-icon": {
            opacity: "0.5",
          },
        }),
        body: ({ theme }) => ({
          color: theme.palette.text.primary,
          fontSize: "0.875rem",
          padding: theme.spacing(2.5, 3),
          minWidth: theme.spacing(15),
          maxWidth: theme.spacing(25),
          borderBottom: `0.3px solid ${theme.palette.grey[300]}`,
          borderTop: "none",
          borderLeft: "none",
          borderRight: "none",
          lineHeight: 1.5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          '& > div[style*="display: flex"]': {
            justifyContent: "center !important",
          },
        }),
        paddingNone: {
          "& > div": {
            display: "flex !important",
            justifyContent: "center !important",
          },
        },
        paddingCheckbox: {
          textAlign: "center",
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: "inherit",
          "&:hover": {
            color: theme.palette.text.primary,
          },
          "&:focus": {
            color: theme.palette.text.primary,
          },
          "&:hover .MuiTableSortLabel-icon": {
            opacity: "0.5",
          },
          "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": {
            opacity: "1",
          },
        }),
        icon: ({ theme }) => ({
          opacity: 0,
          fontSize: "1rem",
          transition: theme.transitions.create("opacity", {
            duration: theme.transitions.duration.shorter,
          }),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          padding: theme.spacing(0.75),
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: theme.spacing(5.625),
          backgroundColor: "transparent",
        }),
        scroller: {
          overflow: "visible",
        },
        flexContainer: ({ theme }) => ({
          gap: theme.spacing(0.5),
        }),
        indicator: ({ theme }) => ({
          height: 2,
          backgroundColor: theme.palette.primary.main,
          borderRadius: `${theme.spacing(0.25)}px ${theme.spacing(0.25)}px 0 0`,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(1.5, 2.5),
          fontSize: "1rem",
          color: theme.palette.text.secondary,
          "&:hover": {
            color: theme.palette.text.primary,
          },
          "&.Mui-selected": {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: ({ theme }) => ({
          backgroundColor: "transparent",
          marginBottom: theme.spacing(0.25),
          marginTop: 0,
        }),
        tabRoot: ({ theme }) => ({
          "&:hover": {
            color: theme.palette.text.primary,
          },
        }),
        defaultTab: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        selected: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: "transparent",
          background: "transparent",
          boxShadow: "none",
          borderBottom: "none",
          padding: theme.spacing(0, 0, 2, 0),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }),
      },
    },
    PluginCatalogTableToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: "transparent",
          boxShadow: "none",
          borderBottom: "none",
          padding: theme.spacing(0, 0, 2, 0),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }),
      },
    },
    PluginCatalogEntityContextMenu: {
      styleOverrides: {
        button: ({ theme }) => ({
          padding: theme.spacing(0.75),
          color: theme.palette.text.secondary,
          "&:hover": {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginBottom: theme.spacing(2),
        }),
      },
    },
    BackstageInfoCard: {
      styleOverrides: {
        header: ({ theme }) => ({
          padding: theme.spacing(2, 2.5),
        }),
      },
    },
    BackstageSignInPage: {
      styleOverrides: {
        item: ({ theme }) => ({
          "& .MuiCardContent-root": {
            padding: theme.spacing(3, 2.5, 2.5),
          },
          "& .MuiCardActions-root": {
            padding: theme.spacing(0, 2.5, 2.5),
          },
        }),
      },
    },
  },
});

export { appviaBrandColors, getThemeConfig, themeConfig };
